<template>
  <div>
    <page-title-bar>
      <v-btn fab dark color="indigo" small @click="isReportSettingsDialogVisible = true">
        <v-icon dark>edit</v-icon>
      </v-btn>
    </page-title-bar>
    <v-container fluid pt-0 grid-list-xl v-if="tempReport">
      <section-tooltip
        :title="tempReport.settings.caption[$i18n.locale]"
        :tooltip="tempReport.settings.description[$i18n.locale]"
      ></section-tooltip>
      <div class="gptReport--viewport" v-bind:style="{ width: tableWidth }">
        <dx-pivot-grid
          ref="grid"
          :data-source="dataSource"
          :allow-sorting-by-summary="true"
          :allow-sorting="true"
          :allow-filtering="true"
          :allow-expand-all="true"
          :show-borders="tempReport.settings.showBorders"
          :show-column-grand-totals="tempReport.settings.showColumnGrandTotals"
          :show-row-grand-totals="tempReport.settings.showRowGrandTotals"
          :show-row-totals="tempReport.settings.showRowTotals"
          :show-column-totals="tempReport.settings.showColumnTotals"
          :rtl-enabled="tempReport.settings.rtlEnabled"
          :rowHeaderLayout="!!tempReport.settings.showRowHeaderAsTree ? 'tree' : 'standard'"
          :state-storing="stateStoring"
          @cellPrepared="onCellPrepared"
          @contentReady="onContentReady"
        >
          <dx-field-chooser :enabled="true" />
          <dx-field-panel
            :enabled="true"
            :visible="tempReport.settings.isFieldPanelVisible"
            :show-data-fields="false"
            :show-row-fields="true"
            :show-column-fields="true"
            :show-filter-fields="false"
          />
          <dx-export
            :enabled="!!tempReport.settings.isExcelExportEnabled"
            :file-name="tempReport.settings.excelExportFileName"
          />
        </dx-pivot-grid>
      </div>
      <report-settings-dialog
        v-if="tempReport !== null"
        :report="tempReport"
        :is-visible="isReportSettingsDialogVisible"
        v-on:close="onReportSettingsDialogClose"
      ></report-settings-dialog>
    </v-container>
  </div>
</template>

<style scoped>
.gptReport--viewport {
    max-width: 100%;
}
.gptReport--viewport .dx-pivotgrid-horizontal-headers {
    background: rgba(221, 221, 221, 0.05);
}
.gptReport--viewport .dx-pivotgrid .dx-pivotgrid-area-data tbody td {
    white-space: normal;
}
</style>

<script>
import { mapGetters } from 'vuex';
import { debounce } from 'lodash';
import ReportSettingsDialog from './ReportSettingsDialog';
import fields from '@/helpers/report-fields.js';

export default {
    components: {
        ReportSettingsDialog
    },
    data() {
        return {
            model: 'tab-settings',
            isReportSettingsDialogVisible: false,
            tableWidth: '100%',
            tempReport: null,
            isTouched: false,
            store: []
        };
    },
    computed: {
        ...mapGetters(['report', 'reportingData']),
        stateStoring: function() {
            const self = this;
            return {
                enabled: true,
                savingTimeout: 100,
                type: 'custom',
                customLoad: () => {
                    return {
                        columnExpandedPaths: self.tempReport.columnExpandedPaths,
                        fields: self.tempReport.fields,
                        rowExpandedPaths: self.tempReport.rowExpandedPaths
                    };
                },
                customSave: state => {
                    if (self.tempReport) {
                        self.tempReport.fields = state.fields;
                        self.tempReport.columnExpandedPaths = state.columnExpandedPaths;
                        self.tempReport.rowExpandedPaths = state.rowExpandedPaths;
                    }
                }
            };
        },
        dataSource: function() {
            return {
                fields,
                store: this.tempReport.settings.decoupleFromSeasonSelection ? this.store : this.reportingData
            };
        }
    },
    watch: {
        report(val) {
            this.tempReport = Object.assign({}, JSON.parse(JSON.stringify(val)));
            if (this.tempReport.settings.decoupleFromSeasonSelection) {
                this.loadReportingData();
            }
        },
        tempReport: {
            handler(newVal, oldVal) {
                if (oldVal !== null) {
                    this.isTouched = true;
                    this.debouncedUpdate(newVal);
                }
            },
            deep: true
        },
        'tempReport.settings': {
            handler(newVal) {
                if (newVal.decoupleFromSeasonSelection) {
                    this.loadReportingData();
                } else {
                    this.store = [];
                }
            },
            deep: true
        }
    },
    methods: {
        // The pivot grid resizes columns at will, we therefore force the
        // columns to use a common width here.
        // @see https://www.devexpress.com/Support/Center/Question/Details/T325911/how-to-change-the-width-of-the-dxpivotgrid-summary-field
        onCellPrepared({ area, cell, cell: { colspan = 1, isWhiteSpace, path = [] }, cellElement }) {
            const { showRowHeaderAsTree } = this.tempReport.settings;
            let width;
            if (area === 'row') {
                if (!showRowHeaderAsTree || isWhiteSpace) {
                    return;
                } else {
                    width = 200 - (path.length - 1) * 20;
                }
            } else {
                width = 120;
            }

            cellElement.style.minWidth = `${width}px`;
            cellElement.style.maxWidth = `${width}px`;
        },

        // Avoid extra wide columns if there are not enough columns to fill
        // the viewport width.
        // @see https://www.devexpress.com/Support/Center/Question/Details/T406912/dxpivotgrid-how-to-change-width-at-runtime
        onContentReady({ component }) {
            const element = component.element();
            const cols = element.querySelectorAll('.dx-area-data-cell colgroup col');
            const dataHeader = element.querySelector('.dx-data-header');

            const tableWidth = cols.length
                ? `${(dataHeader ? dataHeader.offsetWidth : 256) + cols.length * 150}px`
                : '100%';

            if (this.tableWidth !== tableWidth) {
                this.tableWidth = tableWidth;
                this.$nextTick(() => component.repaint());
            }
        },

        onReportSettingsDialogClose() {
            this.isReportSettingsDialogVisible = false;
        },

        loadReportingData() {
            this.$store.dispatch('loadReportingDataComplete').then(result => {
                setTimeout(() => {
                    this.store = result;
                }, 4000);
            });
        }
    },
    created() {
        if (this.$route.params.id) {
            this.$store.dispatch('getReport', { _id: this.$route.params.id });
        } else {
            this.$router.push({ name: 'admin.reports' });
        }

        this.debouncedUpdate = debounce(val => {
            this.$store.dispatch('updateReport', val);
            this.$store.dispatch('getReportsForUser');
        }, 400);

        this.$store.dispatch('getUsers');
    }
};
</script>
